import type { CreateAdminPropertyDto, UpdateAdminPropertyDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminPropertyInput,
  V2UpdateAdminPropertyInput
} from '@idc-forms/schemas/api-v2/admin-properties'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { propertiesAdminV2Service } from '../services/properties-admin-v2.service'

/**
 * Mutaciones de inmuebles contra la API v2. Identifican por id público (`prp_`) y,
 * al completar, invalidan todo el árbol `['properties']` (listado + summary +
 * detalle). El alta/edición vive en el side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreatePropertyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminPropertyInput) =>
      await propertiesAdminV2Service.create(accessToken, body as CreateAdminPropertyDto),
    onSuccess: () => {
      toast.success('Inmueble creado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.properties.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdatePropertyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminPropertyInput }) =>
      await propertiesAdminV2Service.update(accessToken, id, body as UpdateAdminPropertyDto),
    onSuccess: () => {
      toast.success('Inmueble actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.properties.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeletePropertyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await propertiesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Inmueble eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.properties.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
